import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { attachmentDownloadUrl, benefitsClient, logoUrl } from "../apiClient/benefitsClient";
import { useAuth } from "../../gym/auth/useAuth";
import type { Benefit } from "../types/benefit.types";

function formatValidTo(validTo?: string): string | null {
  if (!validTo) return null;
  const d = new Date(`${validTo}T00:00:00`);
  return `Valid until ${d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
}

// Mirrors PIN_NEW_FOR_MS in the backend's benefitRepository.ts — keep in sync.
const NEW_BADGE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

function isRecentlyAdded(createdAt?: string): boolean {
  return createdAt !== undefined && Date.now() - new Date(createdAt).getTime() < NEW_BADGE_WINDOW_MS;
}

function BenefitCard({ benefit }: { benefit: Benefit }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const validityNote = formatValidTo(benefit.validTo);

  return (
    <li className={`benefit-card${isExpanded ? " is-expanded" : ""}`}>
      <button
        type="button"
        className="benefit-card-summary"
        onClick={() => setIsExpanded((e) => !e)}
        aria-expanded={isExpanded}
      >
        {benefit.logoBlobName ? (
          <img className="benefit-logo" src={logoUrl(benefit.id)} alt="" />
        ) : (
          <span className="benefit-logo benefit-logo--placeholder" aria-hidden="true">
            {benefit.merchant.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="benefit-card-text">
          <span className="benefit-merchant">
            {benefit.merchant}
            {isRecentlyAdded(benefit.createdAt) && <span className="benefit-new-badge">New</span>}
          </span>
        </span>
        <span className={`benefit-expand-icon${isExpanded ? " is-open" : ""}`} aria-hidden="true">
          ⌄
        </span>
      </button>

      {isExpanded && (
        <div className="benefit-card-body">
          <p className="benefit-offer-full">{benefit.offerText}</p>
          {benefit.imageUrl && <img className="benefit-image" src={benefit.imageUrl} alt={`${benefit.merchant} offer`} />}
          {benefit.details && <p className="benefit-details">{benefit.details}</p>}
          {benefit.attachments.map((a) => (
            <a key={a.id} className="benefit-attachment-link" href={attachmentDownloadUrl(a.id)} target="_blank" rel="noopener">
              ⬇ Download {a.fileName}
            </a>
          ))}
        </div>
      )}

      {validityNote && <span className="benefit-validity">{validityNote}</span>}
    </li>
  );
}

export function BenefitsPage() {
  const { isAdmin } = useAuth();
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    void benefitsClient
      .getBenefits()
      .then(setBenefits)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load benefits."))
      .finally(() => setIsLoading(false));
  }, []);

  const categories = useMemo(() => [...new Set(benefits.map((b) => b.category))].sort(), [benefits]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return benefits.filter((b) => {
      if (activeCategory && b.category !== activeCategory) return false;
      if (!q) return true;
      return b.merchant.toLowerCase().includes(q) || b.category.toLowerCase().includes(q) || b.offerText.toLowerCase().includes(q);
    });
  }, [benefits, query, activeCategory]);

  // Recently-added benefits get pulled out to their own section at the very
  // top of the page (not just the top of their category) so they're visible
  // to everyone regardless of which category they're browsing. Excluded from
  // their normal category section below so they don't show up twice.
  const recentlyAdded = useMemo(
    () =>
      filtered
        .filter((b) => isRecentlyAdded(b.createdAt))
        .sort((a, b) => (b.createdAt as string).localeCompare(a.createdAt as string)),
    [filtered],
  );

  const byCategory = useMemo(() => {
    const recentIds = new Set(recentlyAdded.map((b) => b.id));
    const map = new Map<string, Benefit[]>();
    for (const b of filtered) {
      if (recentIds.has(b.id)) continue;
      const list = map.get(b.category) ?? [];
      list.push(b);
      map.set(b.category, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered, recentlyAdded]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Benefits</h1>
          <p className="page-sub">Discounts and deals available to Madinet Masr employees.</p>
        </div>
        {isAdmin && (
          <Link to="/benefits/admin" className="btn btn-ghost">
            Manage benefits
          </Link>
        )}
      </header>

      {!isLoading && !error && benefits.length > 0 && (
        <div className="benefits-toolbar">
          <input
            type="text"
            className="coach-search benefits-search"
            placeholder="Search by merchant, category, or offer…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search benefits"
          />
          <div className="benefits-chips">
            <button
              type="button"
              className={`benefits-chip${activeCategory === null ? " is-active" : ""}`}
              onClick={() => setActiveCategory(null)}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                type="button"
                key={c}
                className={`benefits-chip${activeCategory === c ? " is-active" : ""}`}
                onClick={() => setActiveCategory(activeCategory === c ? null : c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {error ? (
        <div className="state-panel">
          <h2>Couldn&rsquo;t load benefits</h2>
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : byCategory.length === 0 && recentlyAdded.length === 0 ? (
        <div className="state-panel">
          <p>{benefits.length === 0 ? "No benefits are available yet — check back soon." : "No benefits match that search."}</p>
        </div>
      ) : (
        <>
          {recentlyAdded.length > 0 && (
            <section className="benefits-category benefits-category--new">
              <h2 className="benefits-category-title benefits-category-title--new">
                New
                <span className="benefits-category-count">{recentlyAdded.length}</span>
              </h2>
              <ul className="benefits-category-items">
                {recentlyAdded.map((b) => (
                  <BenefitCard key={b.id} benefit={b} />
                ))}
              </ul>
            </section>
          )}
          <div className="benefits-categories">
            {byCategory.map(([category, items]) => (
              <section className="benefits-category" key={category}>
                <h2 className="benefits-category-title">
                  {category}
                  <span className="benefits-category-count">{items.length}</span>
                </h2>
                <ul className="benefits-category-items">
                  {items.map((b) => (
                    <BenefitCard key={b.id} benefit={b} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
